// src/pages/VehicleV2/HireVehicleBillApprovalEntry.jsx
// Design template aligned with ProductionBOMListing.jsx
//
// Assumed backend mount:
//   app.use("/api/vehicleBillApproval", vehicleBillApproval);
//
// Backend endpoint used:
//   GET /api/vehicleBillApproval/details?billId=

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaArrowLeft, FaPrint, FaSyncAlt, FaChevronDown, FaChevronUp } from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/vehicleBillApprovalEntry`;

/* ---------------- helpers ---------------- */
const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, d = "") => {
  if (v === null || v === undefined) return d;
  return String(v).trim();
};

const toMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

const StatCell = ({ label, value, isDark }) => (
  <div className="flex flex-col">
    <div
      className={`text-xs font-semibold ${
        isDark ? "text-gray-300" : "text-gray-600"
      }`}
    >
      {label}
    </div>
    <div
      className={`text-sm font-bold ${
        isDark ? "text-white" : "text-gray-900"
      }`}
    >
      {value}
    </div>
  </div>
);

const SectionTitle = ({ title, isDark, tableHeaderClass }) => (
  <div
    className={`px-4 py-2 font-bold uppercase text-xs tracking-wider ${
      tableHeaderClass
        ? `${tableHeaderClass} text-white`
        : isDark
        ? "bg-gray-800 border-y border-gray-700 text-blue-200"
        : "bg-blue-50 border-y border-blue-100 text-blue-900"
    }`}
  >
    {title}
  </div>
);

const HireVehicleBillApprovalEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();
  const location = useLocation();

  const qs = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const billId = toInt(qs.get("billId"), 0);

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS =
    getLS("branchId") || getLS("Branchid") || getLS("branchid") || "0";
  const branchTypeLS =
    getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId") || "0";

  const defaultBranchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim() || "HO",
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim() || "0", [userIdLS]);
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") || userId;

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": defaultBranchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, defaultBranchId, branchType, userId]
  );

  const [showDetails, setShowDetails] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [header, setHeader] = useState({
    billNo: "",
    partyName: "",
    vehicleName: "",
    vehicleType: "",
    vehicleRegNo: "",
    panCardNo: "",
    mailingAddress: "",
    vehicleBasis: "",
    vehicle: "",
    billDate: "",
  });

  const [summary, setSummary] = useState({
    particulars: "",
    month: "",
    ratePerHour: "0.00",
    totalAmount: "0.00",
    totalHr: "0.00",
    totalKm: "0.00",
    breakingHours: "0.00",
    betaOthers: "0.00",
    advance: "0.00",
    totalProgressAmount: "0.00",
    breakingHoursAmount: "0.00",
    grandTotalAmount: "0.00",
    sparesDeduction: "0.00",
    tdsDeduction: "0.00",
    materialTotalAmount: "0.00",
    maintenanceReason: "",
    maintenanceCharges: "0.00",
    otherCharges: "0.00",
    balancePayable: "0.00",
    preparedBy: "",
  });

  const isBusy = pageLoading || detailLoading;

  const demoData = useMemo(
    () => ({
      header: {
        billNo: "BILL/007/0006",
        partyName: "",
        vehicleName: "TIPPER 10W",
        vehicleType: "Tipper 10 Wheeler",
        vehicleRegNo: "MZ01L1303",
        panCardNo: "",
        mailingAddress: "",
        vehicleBasis: "KM",
        vehicle: "",
        billDate: "04/12/2018",
      },
      summary: {
        particulars: "",
        month: "",
        ratePerHour: "0.00",
        totalAmount: "0.00",
        totalHr: "0.00",
        totalKm: "10.00",
        breakingHours: "0.00",
        betaOthers: "0.00",
        advance: "0.00",
        totalProgressAmount: "0.00",
        breakingHoursAmount: "0.00",
        grandTotalAmount: "2000.00",
        sparesDeduction: "0.00",
        tdsDeduction: "20.00",
        materialTotalAmount: "0.00",
        maintenanceReason: "",
        maintenanceCharges: "0.00",
        otherCharges: "0.00",
        balancePayable: "2000.00",
        preparedBy: "",
      },
    }),
    []
  );

  const labelClass = useMemo(
    () =>
      `text-xs font-semibold ${isDark ? "text-gray-300" : "text-gray-600"}`,
    [isDark]
  );

  const valueClass = useMemo(
    () => `text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`,
    [isDark]
  );

  const applyDetails = useCallback((data) => {
    const h = data?.header || {};
    const s = data?.summary || {};

    setHeader({
      billNo: toStr(h.billNo ?? h.fld_billno),
      partyName: toStr(h.partyName ?? h.fld_partyname ?? h.fld_hiredfrom),
      vehicleName: toStr(h.vehicleName ?? h.fld_vehicle_name),
      vehicleType: toStr(
        h.vehicleType ?? h.fld_vehicle_subtypename ?? h.fld_vehicletypename
      ),
      vehicleRegNo: toStr(h.vehicleRegNo ?? h.fld_vehicle_regno),
      panCardNo: toStr(h.panCardNo ?? h.fld_pan_cardno),
      mailingAddress: toStr(h.mailingAddress ?? h.fld_mailingaddress),
      vehicleBasis: toStr(h.vehicleBasis ?? h.fld_vehicle_basistype),
      vehicle: toStr(h.vehicle ?? h.fld_vehicle_code),
      billDate: toStr(h.billDate ?? h.fld_billdate),
    });

    setSummary({
      particulars: toStr(s.particulars ?? s.fld_particulars),
      month: toStr(s.month ?? s.fld_month),
      ratePerHour: toMoney(s.ratePerHour ?? s.fld_rateperhour ?? 0),
      totalAmount: toMoney(s.totalAmount ?? s.fld_total_amount ?? 0),
      totalHr: toMoney(s.totalHr ?? s.fld_total_hr ?? s.fld_total_km_hr ?? 0),
      totalKm: toMoney(s.totalKm ?? s.fld_total_km ?? 0),
      breakingHours: toMoney(s.breakingHours ?? s.fld_breaking_hrs ?? 0),
      betaOthers: toMoney(s.betaOthers ?? s.fld_bata ?? 0),
      advance: toMoney(s.advance ?? s.fld_advance ?? 0),
      totalProgressAmount: toMoney(
        s.totalProgressAmount ?? s.fld_total_progress_amnt ?? 0
      ),
      breakingHoursAmount: toMoney(
        s.breakingHoursAmount ?? s.fld_breaking_amnt ?? 0
      ),
      grandTotalAmount: toMoney(s.grandTotalAmount ?? s.fld_grand_total ?? 0),
      sparesDeduction: toMoney(
        s.sparesDeduction ?? s.fld_deducted_spares_amnt ?? 0
      ),
      tdsDeduction: toMoney(s.tdsDeduction ?? s.fld_tds_amnt ?? 0),
      materialTotalAmount: toMoney(
        s.materialTotalAmount ?? s.fld_total_spares_amnt ?? 0
      ),
      maintenanceReason: toStr(
        s.maintenanceReason ?? s.fld_maintenance_remarks
      ),
      maintenanceCharges: toMoney(
        s.maintenanceCharges ?? s.fld_maintenance_amnt ?? 0
      ),
      otherCharges: toMoney(s.otherCharges ?? s.fld_other_deduction_amnt ?? 0),
      balancePayable: toMoney(s.balancePayable ?? s.fld_balance_payable ?? 0),
      preparedBy: toStr(s.preparedBy ?? s.fld_username ?? s.fld_preparedby),
    });
  }, []);

  const applyDemo = useCallback(() => {
    applyDetails(demoData);
  }, [applyDetails, demoData]);

  const loadDetails = useCallback(async () => {
    if (!billId) {
      applyDemo();
      return;
    }

    try {
      setDetailLoading(true);

      const { data } = await axios.get(`${API_BASE}/details`, {
        params: {
          billId,
          loginUserId,
          branchId: defaultBranchId || 0,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Details load failed");
      }

      applyDetails(data);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load details"
      );
      applyDemo();
    } finally {
      setDetailLoading(false);
    }
  }, [
    billId,
    loginUserId,
    defaultBranchId,
    commonHeaders,
    applyDetails,
    applyDemo,
  ]);

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      await loadDetails();
    } finally {
      setPageLoading(false);
    }
  }, [loadDetails]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleRefresh = useCallback(async () => {
    await loadInit();
  }, [loadInit]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              HIRE VEHICLE BILL APPROVAL
            </span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Hire Vehicle Bill Approval Entry
              </h2>
              <div
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {defaultBranchId || "-"} {" • "} User ID:{" "}
                {loginUserId || "-"}
                {" • "} Branch Type: {branchType || "-"}
                {billId ? ` • Bill ID: ${billId}` : " • Preview Mode"}
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                title="Refresh"
                type="button"
              >
                <FaSyncAlt className={isBusy ? "animate-spin" : ""} />
                Refresh
              </button>

              <button
                onClick={handlePrint}
                disabled={isBusy}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                }`}
                type="button"
              >
                <FaPrint />
                Print
              </button>

              <button
                onClick={() => setShowDetails(!showDetails)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
                type="button"
              >
                {showDetails ? <FaChevronUp /> : <FaChevronDown />}
                {showDetails ? "Hide Details" : "Show Details"}
              </button>
            </div>
          </div>

          {/* ==================== BILL INFO ==================== */}
          {showDetails && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Bill Information
                </h3>
                <span
                  className={`text-sm font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {isBusy ? "Loading..." : "Ready"}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <div
                      className={`mb-1 text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Bill No
                    </div>
                    <div className={valueClass}>{header.billNo || "-"}</div>
                  </div>
                  <div>
                    <div
                      className={`mb-1 text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Name of the Party
                    </div>
                    <div className={valueClass}>{header.partyName || "-"}</div>
                  </div>
                  <div>
                    <div
                      className={`mb-1 text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Vehicle Name
                    </div>
                    <div className={valueClass}>{header.vehicleName || "-"}</div>
                  </div>
                  <div>
                    <div
                      className={`mb-1 text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Vehicle Type
                    </div>
                    <div className={valueClass}>{header.vehicleType || "-"}</div>
                  </div>
                  <div>
                    <div
                      className={`mb-1 text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Vehicle RegNo
                    </div>
                    <div className={valueClass}>
                      {header.vehicleRegNo || "-"}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <div
                      className={`mb-1 text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      PAN Card No
                    </div>
                    <div className={valueClass}>{header.panCardNo || "-"}</div>
                  </div>
                  <div>
                    <div
                      className={`mb-1 text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Mailing Address
                    </div>
                    <div className={`${valueClass} break-words`}>
                      {header.mailingAddress || "-"}
                    </div>
                  </div>
                  <div>
                    <div
                      className={`mb-1 text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Vehicle Basis
                    </div>
                    <div className={valueClass}>
                      {header.vehicleBasis || "-"}
                    </div>
                  </div>
                  <div>
                    <div
                      className={`mb-1 text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Vehicle
                    </div>
                    <div className={valueClass}>{header.vehicle || "-"}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {isBusy ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {pageLoading ? "Loading page..." : "Loading details..."}
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Particulars
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">
                        Month
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-semibold">
                        Rate per Hour
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-semibold">
                        Total Amount
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${
                      isDark ? "divide-gray-700" : "divide-gray-200"
                    } divide-y`}
                  >
                    <tr
                      className={`${
                        isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-blue-50"
                      } transition-colors`}
                    >
                      <td className="px-6 py-4">
                        <div className={labelClass}>Bill Date</div>
                        <div
                          className={`text-sm font-medium ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {header.billDate || "-"}
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 text-center text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {summary.month || "-"}
                      </td>
                      <td
                        className={`px-6 py-4 text-right text-sm font-semibold ${
                          isDark ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        {summary.ratePerHour}
                      </td>
                      <td
                        className={`px-6 py-4 text-right text-sm font-semibold ${
                          isDark ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        {summary.totalAmount}
                      </td>
                    </tr>

                    <tr
                      className={`${
                        isDark ? "bg-gray-750 hover:bg-gray-700" : "bg-gray-50 hover:bg-blue-50"
                      } transition-colors`}
                    >
                      <td colSpan={2} className="px-6 py-4">
                        <div className="grid grid-cols-3 gap-6">
                          <StatCell
                            label="Total Hr"
                            value={summary.totalHr}
                            isDark={isDark}
                          />
                          <StatCell
                            label="Total KM"
                            value={summary.totalKm}
                            isDark={isDark}
                          />
                          <StatCell
                            label="Breaking Hours"
                            value={summary.breakingHours}
                            isDark={isDark}
                          />
                        </div>
                      </td>
                      <td colSpan={2} className="px-6 py-4">
                        <div className="grid grid-cols-1 gap-2">
                          <div className="flex items-center justify-between">
                            <span className={labelClass}>Beta (Others)</span>
                            <span className={valueClass}>
                              {summary.betaOthers}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className={labelClass}>Advance</span>
                            <span className={valueClass}>{summary.advance}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className={labelClass}>Total Amount</span>
                            <span className={valueClass}>
                              {summary.totalProgressAmount}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className={labelClass}>
                              Breaking Hours Amount
                            </span>
                            <span className={valueClass}>
                              {summary.breakingHoursAmount}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className={`${labelClass} uppercase`}>
                              Total Amount
                            </span>
                            <span
                              className={`text-base font-bold ${
                                isDark ? "text-green-400" : "text-green-600"
                              }`}
                            >
                              {summary.grandTotalAmount}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <SectionTitle
                  title="Material Deductions"
                  isDark={isDark}
                  tableHeaderClass={tableHeaderClass}
                />
                <div
                  className={`px-6 py-4 ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-3xl ml-auto">
                    <div className="flex items-center justify-between">
                      <span className={labelClass}>Spares Deductions</span>
                      <span className={valueClass}>
                        {summary.sparesDeduction}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={labelClass}>TDS Deductions</span>
                      <span className={valueClass}>{summary.tdsDeduction}</span>
                    </div>
                    <div className="flex items-center justify-between md:col-span-2">
                      <span className={`${labelClass} uppercase`}>
                        Total Amount
                      </span>
                      <span className={valueClass}>
                        {summary.materialTotalAmount}
                      </span>
                    </div>
                  </div>
                </div>

                <SectionTitle
                  title="Maintenance Reason"
                  isDark={isDark}
                  tableHeaderClass={tableHeaderClass}
                />
                <div
                  className={`px-6 py-4 ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div
                        className={`mb-1 text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Maintenance Reason
                      </div>
                      <div className={valueClass}>
                        {summary.maintenanceReason || "-"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={`mb-1 text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Maintenance Charges
                      </div>
                      <div
                        className={`text-sm font-semibold ${
                          isDark ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        {summary.maintenanceCharges}
                      </div>
                    </div>
                  </div>
                </div>

                <SectionTitle
                  title="Deduction"
                  isDark={isDark}
                  tableHeaderClass={tableHeaderClass}
                />
                <div
                  className={`px-6 py-4 ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-3xl ml-auto">
                    <div className="flex items-center justify-between">
                      <span className={labelClass}>Other Charges</span>
                      <span className={valueClass}>{summary.otherCharges}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`${labelClass} uppercase`}>
                        Balance Payable
                      </span>
                      <span
                        className={`text-base font-bold ${
                          isDark ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        {summary.balancePayable}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  className={`px-6 py-4 border-t ${
                    isDark
                      ? "bg-gray-800 border-gray-700"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div
                    className={`mb-1 text-sm font-medium ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Prepared by
                  </div>
                  <div className={valueClass}>{summary.preparedBy || "-"}</div>
                </div>
              </>
            )}
          </div>

          {/* ==================== BACK ==================== */}
          <div className="flex justify-start mt-6">
            <button
              onClick={handleBack}
              disabled={isBusy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              } ${isBusy ? "opacity-50 cursor-not-allowed" : ""}`}
              type="button"
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default HireVehicleBillApprovalEntry;